import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  CircularProgress,
  Alert,
  Link as MuiLink
} from '@mui/material';
import { Check, ArrowForward, Settings } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import pricingService from '../../services/pricingService';
import axios from 'axios';

function PricingPanel() {
  const { user, isAdmin } = useAuth();
  const [pricingTiers, setPricingTiers] = useState([]);
  const [currentTier, setCurrentTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        setLoading(true);
        setError(null);
        const tiers = await pricingService.getPricingTiers();
        console.log("Dashboard - fetched pricing tiers:", tiers);
        
        if (!Array.isArray(tiers) || tiers.length === 0) {
          console.warn("Invalid pricing tiers data received");
          throw new Error("No pricing tiers available");
        }
        
        // Sort by price
        const sortedTiers = tiers.sort((a, b) => a.basePrice - b.basePrice);
        setPricingTiers(sortedTiers);
        
        // Find user's current tier
        if (user && user.serviceTier) {
          const userTier = sortedTiers.find(tier => tier.name === user.serviceTier);
          setCurrentTier(userTier || sortedTiers[0]);
        } else {
          setCurrentTier(sortedTiers[0]);
        }
      } catch (err) {
        console.error('Error fetching pricing data:', err);
        setError('Pricing information is currently unavailable. Please try again later or contact support.');
        setPricingTiers([]);
        setCurrentTier(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPricingData();
  }, [user]);

  const handleUpgradeClick = async () => {
    try {
      const response = await axios.post('/api/billing/create-checkout-session', {
        newTier: nextTier.name,
        successUrl: `${window.location.origin}/dashboard`,
        cancelUrl: `${window.location.origin}/dashboard`
      });

      if (response.data.status === 'success' && response.data.data.url) {
        window.location.href = response.data.data.url;
      } else {
        throw new Error(response.data.message || 'Failed to create checkout session');
      }
    } catch (err) {
      console.error('Error initiating upgrade:', err);
      setError(err.response?.data?.message || err.message || 'Failed to initiate upgrade');
    }
  };

  if (loading) {
    return (
      <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Typography variant="body2">
          We're unable to retrieve pricing information at this time. Please try again later or contact support.
        </Typography>
        <Button 
          component={Link} 
          to="/contact" 
          variant="outlined" 
          size="small"
          sx={{ mt: 2 }}
        >
          Contact Support
        </Button>
      </Paper>
    );
  }

  if (!currentTier) {
    return (
      <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
        <Alert severity="warning" sx={{ mb: 2 }}>No pricing information available</Alert>
        <Typography variant="body2">
          We couldn't retrieve your pricing information from our database. 
          Please contact support if this issue persists.
        </Typography>
        <Button 
          component={Link} 
          to="/contact" 
          variant="outlined" 
          size="small"
          sx={{ mt: 2 }}
        >
          Contact Support
        </Button>
      </Paper>
    );
  }

  // Get next tier (for upgrade suggestion)
  const currentTierIndex = pricingTiers.findIndex(tier => tier.name === currentTier.name);
  const nextTier = currentTierIndex < pricingTiers.length - 1 ? pricingTiers[currentTierIndex + 1] : null;

  return (
    <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Pricing & Plans</Typography>
        {isAdmin && (
          <Button 
            component={Link} 
            to="/admin#pricing" 
            size="small" 
            startIcon={<Settings />}
            variant="outlined"
          >
            Configure
          </Button>
        )}
      </Box>
      <Divider sx={{ mb: 3 }} />
      
      <Grid container spacing={3}>
        {/* Current Plan */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">Current Plan</Typography>
                <Chip 
                  label={currentTier.displayName} 
                  color={
                    currentTier.name === 'free' ? 'default' : 
                    currentTier.name === 'professional' ? 'primary' : 
                    'secondary'
                  } 
                  size="small"
                />
              </Box>
              
              <Typography variant="body2" paragraph>
                {pricingService.formatCurrency(currentTier.basePrice)} per month
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <List dense>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Check color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={`${currentTier.monthlyLimit?.toLocaleString() || 0} API calls/month`} 
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Check color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={`Up to ${currentTier.apiKeyLimit || 1} API ${(currentTier.apiKeyLimit || 1) === 1 ? 'Key' : 'Keys'}`} 
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                {currentTier.features && currentTier.features.slice(0, 3).map((feature, idx) => (
                  <ListItem key={idx} disablePadding>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Check color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={feature} 
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Upgrade Option */}
        <Grid item xs={12} md={6}>
          {nextTier ? (
            <Card variant="outlined" sx={{ height: '100%', bgcolor: 'action.hover' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">Upgrade To</Typography>
                  <Chip 
                    label={nextTier.displayName} 
                    color={nextTier.name === 'professional' ? 'primary' : 'secondary'} 
                    size="small"
                  />
                </Box>
                
                <Typography variant="body2" paragraph>
                  {pricingService.formatCurrency(nextTier.basePrice)} per month
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <List dense>
                  <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Check color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={`${nextTier.monthlyLimit.toLocaleString()} API calls/month`} 
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Check color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={`Up to ${nextTier.apiKeyLimit} API Keys`} 
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  {nextTier.features && nextTier.features.slice(0, 3).map((feature, idx) => (
                    <ListItem key={idx} disablePadding>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Check color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={feature} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
                
                <Button
                  onClick={handleUpgradeClick}
                  variant="contained"
                  color="primary"
                  size="small"
                  endIcon={<ArrowForward />}
                  sx={{ mt: 2 }}
                >
                  Upgrade Now
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card variant="outlined" sx={{ height: '100%', bgcolor: 'action.hover' }}>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">Enterprise Plan</Typography>
                  <Typography variant="body2">You're on our top tier plan!</Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="body2" paragraph>
                  Need more API calls or custom features? We offer tailored solutions for enterprise needs.
                </Typography>
                
                <Button
                  component={Link}
                  to="/contact"
                  variant="outlined"
                  size="small"
                  endIcon={<ArrowForward />}
                  sx={{ mt: 2 }}
                >
                  Contact Sales
                </Button>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          View our{' '}
          <MuiLink component={Link} to="/pricing">
            full pricing details
          </MuiLink>
          {' '}or{' '}
          <MuiLink component={Link} to="/contact">
            contact us
          </MuiLink>
          {' '}with any questions.
        </Typography>
      </Box>
    </Paper>
  );
}

export default PricingPanel; 